import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Flag, Rocket, ArrowRight } from "lucide-react";
import { SkillsTranslator } from "@/components/SkillsTranslator";
import { VeteranDiscountBanner } from "@/components/VeteranDiscountBanner";

const DOORS = [
  {
    icon: Rocket, tag: "Still in uniform",
    title: "Transitioning Service Members",
    body: "Separating soon? Build and launch a real business before your ETS date — a structured, cohort-friendly program that turns your remaining time in service into a running business.",
    to: "/veterans/transitioning", cta: "Explore the transition program",
  },
  {
    icon: Flag, tag: "Already separated",
    title: "Veterans & Retirees",
    body: "Out of the service and ready to build? Start or grow your business at your own pace — with help on veteran certifications (SDVOSB/VOSB) and veteran-specific funding.",
    to: "/veterans/prior-service", cta: "Explore veteran resources",
  },
];

export default function VeteransHub() {
  return (
    <div data-testid="veterans-hub-page">
      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden grain">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-5"><ShieldCheck size={15} /> For those who served</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white leading-[1.05] max-w-4xl">
              Turn your service into <span className="text-gold">your next mission.</span>
            </h1>
            <p className="text-base md:text-lg text-gray-300 font-light mt-6 max-w-2xl leading-relaxed">
              Whether you're preparing to leave the military or you separated years ago, ATF WebWorks helps you build, launch and grow a real business — the same platform, tailored to where you are.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Two doors */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-2 gap-6">
          {DOORS.map((d, i) => (
            <motion.div key={d.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}>
              <Link to={d.to} data-testid={`veterans-door-${i}`}
                className="group flex flex-col h-full border border-white/10 bg-navy-800 p-8 transition-all hover:border-gold/50 hover:-translate-y-1">
                <div className="flex items-center justify-between mb-5">
                  <div className="w-12 h-12 border border-gold/40 flex items-center justify-center text-gold transition-colors group-hover:bg-gold group-hover:text-navy"><d.icon size={22} /></div>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-gray-500 border border-white/10 px-2.5 py-1">{d.tag}</span>
                </div>
                <h2 className="font-heading text-2xl text-white mb-3 leading-tight">{d.title}</h2>
                <p className="text-gray-400 text-sm leading-relaxed flex-1">{d.body}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-gold text-sm font-semibold">{d.cta} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></span>
              </Link>
            </motion.div>
          ))}
        </div>
        <div className="mt-8"><VeteranDiscountBanner /></div>
      </section>

      <SkillsTranslator />

      {/* Founder */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-4"><Flag size={15} /> Built by a veteran</div>
        <h2 className="font-heading text-3xl text-white mb-4 max-w-3xl">A mission worth serving</h2>
        <p className="text-gray-300 leading-relaxed max-w-3xl">
          Founded by a retired service member with 26 years of active-duty experience, ATF WebWorks understands the transition firsthand. Our goal is simple: help those who served replace uncertainty with opportunity by turning military experience into successful business ownership.
        </p>
      </section>
    </div>
  );
}
